import Paper from 'raydiant-elements/core/Paper';
import PaperModal from 'raydiant-elements/core/PaperModal';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import Heading1 from 'raydiant-elements/typography/Heading1';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import Center from 'raydiant-elements/layout/Center';
import { FC, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  selectUserProfile,
  selectIsRaydiantSuperAdmin,
  selectIsCustomerSupport,
  selectIsEnterpriseUser,
  selectIsProUser,
} from '../../selectors/user';
import { selectDomainForCurrentUser } from '../../selectors/v2/domains';
import {
  canEditDomain,
  getMasqueradeUser,
  getRemoteAssistUser,
} from '../../utilities';
import * as paths from '../../routes/paths';
import Page from '../../components/Page';
import { loadAccountPage } from './actions';
import useStyles from './AccountPage.styles';
import AccountDetails from './AccountDetails';
import Masquerade from './Masquerade';
import RemoteAssistance from './RemoteAssistance';
import AccountSettings from './AccountSettings';
import DomainSettings from './DomainSettings';
import MemberAccounts from './MemberAccounts';
import MemberAccountEdit from './MemberAccountEdit';
import EnableMultiTimezone from './EnableMultiTimezone/EnableMultiTimezone';
import classNames from 'classnames';
import MemberAccountsV2 from './MemberAccountsV2';
import Button from 'raydiant-elements/core/Button';
import InputLabel from 'raydiant-elements/core/InputLabel';
import Select from 'raydiant-elements/core/Select';
import useBillingAccounts from '../../hooks/useBillingAccounts';
import useCreateBillingPortalSession from '../../hooks/useCreateBillingPortalSession';
import { useToastState } from '@raydiant/raydial/components/Toast';
import analytics from '../../clients/analyticsClient';

interface AccountPageProps {}

const AccountPage: FC<AccountPageProps> = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const state = useToastState();
  const { t } = useTranslation();

  const match = useRouteMatch<{ profileId?: string }>({
    path: paths.accountDomainMembers.pattern,
  });
  const selectedProfileId = match?.params?.profileId ?? '';

  // Queries
  const billingAccountsQuery = useBillingAccounts();
  const createBillingPortalSession = useCreateBillingPortalSession();
  const [selectedBillingAccountId, setSelectedBillingAccountId] = useState('');

  // Selectors

  const profile = useSelector(selectUserProfile);
  const domain = useSelector(selectDomainForCurrentUser);
  const isRaydiantSuperAdmin = useSelector(selectIsRaydiantSuperAdmin);
  const isCustomerSupport = useSelector(selectIsCustomerSupport);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const isMultiTimezoneEnabled = profile?.isMultiTimezoneEnabled;
  const isProUser = useSelector(selectIsProUser);

  const billingAccounts = useMemo(
    () => billingAccountsQuery.data ?? [],
    [billingAccountsQuery.data],
  );

  // Side-effects

  const usersPageTracked = useRef(false);
  useEffect(() => {
    if (
      !usersPageTracked.current &&
      profile &&
      canEditDomain(profile) &&
      isEnterpriseUser
    ) {
      usersPageTracked.current = true;
      analytics.trackUsersPageOpened({ pageName: 'account_settings' });
    }
  }, [profile, isEnterpriseUser]);

  useEffect(() => {
    // segment tracking
    analytics.trackPageView({ page: 'account_settings' });
    dispatch(loadAccountPage());
  }, [dispatch]);

  // Default to first billing account when list loads or selection is no longer valid
  useEffect(() => {
    if (billingAccounts.length === 0) {
      setSelectedBillingAccountId('');
      return;
    }
    const isCurrentValid = billingAccounts.some(
      (a) => a.id === selectedBillingAccountId,
    );
    if (!isCurrentValid) {
      setSelectedBillingAccountId(billingAccounts[0].id);
    }
  }, [billingAccounts, selectedBillingAccountId]);

  // Callbacks

  const setSelectedProfileId = useCallback(
    (profileId: string | null) => {
      if (profileId) {
        history.push(paths.accountDomainMembers(profileId));
      } else {
        history.push(paths.account());
      }
    },
    [history],
  );

  const handleBillingPortal = async () => {
    if (!selectedBillingAccountId) return;
    const selectedAccount = billingAccounts.find(
      (a) => a.id === selectedBillingAccountId,
    );
    analytics.trackManageSubscriptionClicked({
      page: 'account_settings',
      billingAccountId: selectedBillingAccountId,
      billingAccountName: selectedAccount?.name ?? '',
    });
    try {
      const result = await createBillingPortalSession.mutateAsync(
        selectedBillingAccountId,
      );
      if (result?.url) {
        window.location.href = result.url;
      } else {
        console.error('Billing portal session created without URL', result);
        state.add({
          title: 'Oops! Something went wrong',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    } catch (error) {
      state.add({
        title: 'Oops! Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  };

  const hasBillingAccounts = billingAccounts.length > 0;

  // Render

  if (!profile || (profile?.domainId && !domain)) {
    return (
      <Page title="Account">
        <Center className="h-full">
          <CircularProgress size={30} />
        </Center>
      </Page>
    );
  }

  const isDomainEditable = canEditDomain(profile) && isEnterpriseUser;
  const isMasqueradeEnabled = isRaydiantSuperAdmin || !!getMasqueradeUser();
  const isRemoteAssistanceEnabled =
    isCustomerSupport || !!getRemoteAssistUser();
  const selectedProfile = (domain?.r.profiles || []).find(
    (p) => p.id === selectedProfileId,
  );
  const showV2MemberAccounts = !isMasqueradeEnabled;

  return (
    <Page title="Account">
      <Scrollable>
        <div className={classNames(classes.container, 'mt-3')}>
          <div
            className={cn(
              classes.account,
              !isDomainEditable && classes.accountNoDomain,
            )}
          >
            <Paper className={classes.section} color="light">
              <div className={classes.sectionHeader}>
                <Heading1>Account</Heading1>
              </div>
              <div className={classes.sectionBody}>
                <AccountDetails />
                <br />
                <br />
                <div className="flex flex-col gap-4 mb-10">
                  <div className="flex gap-2 flex-wrap items-center">
                    <div className="flex flex-col gap-1 w-full">
                      <InputLabel>{t('accountPage.billingAccount')}</InputLabel>
                      <Select
                        value={selectedBillingAccountId}
                        onChange={setSelectedBillingAccountId}
                        onOpen={() =>
                          analytics.trackBillingAccountsDropdownClicked({
                            page: 'account_settings',
                          })
                        }
                        displayEmpty
                        disabled={!hasBillingAccounts}
                        aria-label={t('accountPage.billingAccount')}
                      >
                        {hasBillingAccounts ? (
                          billingAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))
                        ) : (
                          <option value="">
                            {t('accountPage.noBillingAccountAdded')}
                          </option>
                        )}
                      </Select>
                    </div>
                    <Button
                      onClick={handleBillingPortal}
                      label={t('accountPage.managePayments')}
                      color="progress"
                      type="button"
                      disabled={
                        !hasBillingAccounts ||
                        createBillingPortalSession.isLoading
                      }
                      fullWidth
                    />
                  </div>
                  {isProUser && (
                    <Button
                      onClick={() => {
                        // segment tracking
                        analytics.trackGetPlanCTAClicked({
                          page: 'account_settings',
                          buttonLocation: 'account_settings',
                        });
                        history.push(paths.plans('pro'));
                      }}
                      label={t('getPlan')}
                      color="primary"
                      type="button"
                    />
                  )}
                </div>
                {!isMultiTimezoneEnabled && !isEnterpriseUser && (
                  <>
                    <EnableMultiTimezone />
                    <br />
                    <br />
                  </>
                )}
                <AccountSettings />
              </div>
              {isMasqueradeEnabled && (
                <div className={classes.sectionBody}>
                  <Masquerade />
                </div>
              )}
              {isRemoteAssistanceEnabled && (
                <div className={classes.sectionBody}>
                  <RemoteAssistance />
                </div>
              )}
            </Paper>
          </div>

          {isDomainEditable && (
            <div className={classes.domain}>
              <Paper className={classes.section} color="light">
                <div className={classes.sectionHeader}>
                  <Heading1>Enterprise Domain</Heading1>
                </div>
                <div className={classes.domainInner}>
                  <div className={classes.domainInfo}>
                    <div className={classes.sectionBody}>
                      <DomainSettings />
                    </div>
                    <div
                      className={
                        showV2MemberAccounts
                          ? classes.sectionBody2
                          : classes.sectionBody
                      }
                    >
                      {showV2MemberAccounts ? (
                        <MemberAccountsV2 />
                      ) : (
                        <MemberAccounts
                          selectedProfileId={selectedProfileId}
                          onEditProfile={setSelectedProfileId}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Paper>
            </div>
          )}

          <PaperModal
            color="lightGrey"
            className={classes.profile}
            open={!!selectedProfile}
            onClose={() => setSelectedProfileId(null)}
          >
            {selectedProfile && <MemberAccountEdit profile={selectedProfile} />}
          </PaperModal>
        </div>
      </Scrollable>
    </Page>
  );
};

export default AccountPage;
